import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import { useCurrentUser } from '@/app/providers/user';
import { logger } from '@/shared/utils/logger';
import {
  addItemToShelf,
  countsByShelf,
  createShelf,
  deleteShelf,
  getMembership,
  listShelves,
  recolorShelf,
  removeItemFromShelf,
  renameShelf,
  toggleFavorite,
  type Shelf,
  type ShelfColor,
  type ShelfMembership,
} from '@/features/library/services/shelfService';

/*
 * One collection, shared by every surface showing it.
 *
 * This lived in `useState` and so was three collections, not one: the library
 * panel, the prompt page and the sidebar each held a private copy. The sidebar
 * is the one that hurt — it lives in AppLayout and never remounts, so a shelf
 * created anywhere else stayed invisible in its `Move to shelf ›` menu for the
 * rest of the session, and a deleted one stayed selectable there, writing
 * membership rows against a shelf that no longer existed.
 *
 * A module store rather than a provider because nothing about this needs to be
 * positioned in the tree: there is exactly one signed-in user and one set of
 * shelves, and a provider would mean wiring App and every test that renders one
 * of the three surfaces. `useSyncExternalStore` is the primitive for precisely
 * this — an external mutable source read by many components.
 */
type ShelvesState = {
  /** Whose shelves these are; also the guard against three mounts firing three loads. */
  userId: string | null;
  shelves: Shelf[];
  membership: ShelfMembership;
  loading: boolean;
};

const EMPTY: ShelvesState = { userId: null, shelves: [], membership: {}, loading: true };

let state: ShelvesState = EMPTY;
const listeners = new Set<() => void>();

/* The snapshot identity only changes here, which is what `useSyncExternalStore`
   compares — returning a fresh object per read would re-render forever. */
function setState(next: Partial<ShelvesState>) {
  state = { ...state, ...next };
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    /* The cache lives exactly as long as something is displaying it. In the app
       that is the whole session (the sidebar never unmounts); in a test it is
       one case, so nothing leaks into the next. It also means a sign-out that
       tears the shell down cannot leave one user's shelves in front of the
       next. */
    if (listeners.size === 0) state = EMPTY;
  };
}

const getSnapshot = () => state;

/**
 * Shelves and membership for the signed-in user.
 *
 * Three surfaces need this — the library's Browse-by panel, the content title's
 * `Move to shelf ›` menu, and the sidebar's per-job menu. Every mutation returns
 * the fresh collection from the service and is applied wholesale, which keeps
 * this honest when the service becomes a real endpoint: no optimistic local
 * patching to unwind on failure.
 */
export function useShelves(itemIds: string[] = []) {
  const { user } = useCurrentUser();
  const userId = user?.userId;

  const { shelves, membership, loading } = useSyncExternalStore(subscribe, getSnapshot);

  useEffect(() => {
    if (!userId) {
      setState({ userId: null, shelves: [], membership: {}, loading: false });
      return;
    }
    // Another surface is already showing (or fetching) this user's shelves.
    // Claimed before the await so three simultaneous mounts make one request.
    if (state.userId === userId) return;
    setState({ userId, loading: true });

    void Promise.all([listShelves(userId), getMembership(userId)])
      .then(([nextShelves, nextMembership]) => {
        if (state.userId !== userId) return;
        setState({ shelves: nextShelves, membership: nextMembership });
      })
      .catch((err) => {
        /* There was no catch here at all. A rejection left `loading` true
           permanently — the Browse-by rail stuck on its skeleton, no error, no
           retry, nothing to indicate anything had happened — and raised an
           unhandled rejection besides. Falling back to empty is honest: it says
           "no shelves" rather than "still loading" forever. */
        if (state.userId !== userId) return;
        logger.error('Failed to load shelves', err);
        setState({ shelves: [], membership: {} });
      })
      .finally(() => {
        if (state.userId === userId) setState({ loading: false });
      });
  }, [userId]);

  /* `itemIds` is a fresh array on every render of the caller, so the counts need
     a stable identity to memo against or they recompute on each keystroke in the
     library search box. JSON rather than `join('|')`: a join invents a delimiter,
     and any id containing it would come back out of the split as two wrong ids.
     Nothing generates such an id today, which is exactly why it would be found
     the hard way. The key is only an identity — the real array is passed through. */
  const itemIdKey = JSON.stringify(itemIds);
  const counts = useMemo(
    () => countsByShelf(membership, itemIds),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- itemIdKey *is* itemIds' identity
    [membership, itemIdKey],
  );

  const guard = useCallback(
    <A extends unknown[], R>(fn: (userId: string, ...args: A) => Promise<R>) =>
      async (...args: A): Promise<R | undefined> => {
        if (!userId) return undefined;
        return fn(userId, ...args);
      },
    [userId],
  );

  const actions = useMemo(
    () => ({
      create: async (name: string, color?: ShelfColor) => {
        const next = await guard(createShelf)(name, color);
        if (next) setState({ shelves: next });
      },
      rename: async (shelfId: string, name: string) => {
        const next = await guard(renameShelf)(shelfId, name);
        if (next) setState({ shelves: next });
      },
      recolor: async (shelfId: string, color: ShelfColor) => {
        const next = await guard(recolorShelf)(shelfId, color);
        if (next) setState({ shelves: next });
      },
      remove: async (shelfId: string) => {
        const next = await guard(deleteShelf)(shelfId);
        if (!next || !userId) return;
        /* One setState for both, after both reads. Publishing the pruned
           shelves first and awaiting the membership re-read second put a
           snapshot in front of every subscriber where the shelf was gone but
           membership still named it — checkmarks against a shelf that no
           longer exists. deleteShelf prunes the rows synchronously with the
           delete, so reading then publishing once is atomic from the outside. */
        const membership = await getMembership(userId);
        setState({ shelves: next, membership });
      },
      addTo: async (itemId: string, shelfId: string) => {
        const next = await guard(addItemToShelf)(itemId, shelfId);
        if (next) setState({ membership: next });
      },
      removeFrom: async (itemId: string, shelfId: string) => {
        const next = await guard(removeItemFromShelf)(itemId, shelfId);
        if (next) setState({ membership: next });
      },
      toggleFavorite: async (itemId: string) => {
        const next = await guard(toggleFavorite)(itemId);
        if (next) setState({ membership: next });
      },
    }),
    [guard, userId],
  );

  return { shelves, membership, counts, loading, ...actions };
}
